'use client'

/**
 * src/components/TwoFactorSection.tsx
 *
 * Settings → "Two-factor authentication" — enable / disable TOTP 2FA.
 *
 * Flow:
 *   1. Click "Enable" -> POST /api/me/2fa/setup -> server returns
 *      otpauthUrl + base32 secret + 10 recovery codes.
 *   2. UI shows: the otpauth URL (copy-paste into authenticator app
 *      OR scan as QR — we don't render QR in v1 to avoid a new
 *      `qrcode` dep), the base32 secret as a fallback for manual
 *      entry, and the recovery codes (only time they're visible).
 *   3. User types a 6-digit code from their app -> POST /api/me/2fa/enable.
 *   4. Server verifies + flips totpEnabled=true.
 *
 * Disable flow: same shape — type a current code (or recovery code)
 * to confirm, then POST /api/me/2fa/disable. Clears the secret.
 *
 * No QR rendering in v1. Most authenticator apps support manual entry
 * of the secret (Google Authenticator: "Enter setup key"). When we
 * add QR, swap in `qrcode` and render the otpauthUrl.
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'

type Status = 'loading' | 'off' | 'setup-pending' | 'on'

type SetupResponse = {
  secret: string
  otpauthUrl: string
  recoveryCodes: string[]
}

export default function TwoFactorSection({ initialEnabled }: { initialEnabled: boolean }) {
  const [status, setStatus] = useState<Status>(initialEnabled ? 'on' : 'off')
  const [setupData, setSetupData] = useState<SetupResponse | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function startSetup() {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/me/2fa/setup', {
        method: 'POST',
        cache: 'no-store',
      })
      if (!res.ok) {
        setError('Could not start 2FA setup. Please try again.')
        return
      }
      const data: SetupResponse = await res.json()
      setSetupData(data)
      setStatus('setup-pending')
    } catch {
      setError('Network error.')
    } finally {
      setBusy(false)
    }
  }

  async function confirmEnable() {
    if (!code.trim()) return
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/me/2fa/enable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({ code: code.trim() }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(
          data?.error === 'invalid_code'
            ? "That code didn't match. Try again."
            : 'Could not enable 2FA. Please try again.',
        )
        return
      }
      setStatus('on')
      // Keep setupData briefly visible so the user has a chance to
      // copy recovery codes before we clear; or they can leave the
      // page. Clearing here is intentional.
      setSetupData(null)
      setCode('')
    } catch {
      setError('Network error.')
    } finally {
      setBusy(false)
    }
  }

  async function confirmDisable() {
    if (!code.trim()) return
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/me/2fa/disable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({ code: code.trim() }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setError(
          data?.error === 'invalid_code'
            ? "That code didn't match. Try a current code from your authenticator, or a recovery code."
            : 'Could not disable 2FA. Please try again.',
        )
        return
      }
      setStatus('off')
      setSetupData(null)
      setCode('')
    } catch {
      setError('Network error.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div className="text-sm text-muted-foreground">
        Add a second login factor for credentials sign-in. Use any authenticator app (Google
        Authenticator, 1Password, Authy). Google sign-in is unaffected — Google enforces 2FA at the
        Google account level.
      </div>

      {error && <p className="mt-2 text-sm text-rose-700 dark:text-rose-300">{error}</p>}

      {status === 'off' && (
        <div className="mt-3">
          <Button
            type="button"
            onClick={startSetup}
            disabled={busy}
            variant="outline"
            className="border-brand/40 text-brand-ink hover:bg-brand/10 dark:text-brand-ink"
          >
            {busy ? 'Starting…' : 'Enable two-factor authentication'}
          </Button>
        </div>
      )}

      {status === 'setup-pending' && setupData && (
        <div className="mt-3 space-y-3 rounded-md border border-brand/30 bg-brand/5 p-3">
          <div>
            <div className="text-sm font-medium">1. Scan with your authenticator app</div>
            <p className="mt-1 text-xs text-muted-foreground">
              Open your authenticator app, add a new account, and scan this QR. Or use the
              manual-entry secret below.
            </p>
            {/* The /api/me/2fa/qr endpoint server-renders an SVG of
                the otpauth URL. The secret stays server-side; the QR
                image is the only thing that crosses the wire. We use
                a plain <img> (not next/image) since the response is
                non-cacheable + per-user. */}
            <div className="mt-2 flex items-center justify-center rounded bg-white p-3">
              {}
              <img
                src="/api/me/2fa/qr"
                alt="TOTP QR code — scan with your authenticator app"
                width={240}
                height={240}
                className="block"
              />
            </div>
            <div className="mt-2">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Manual entry secret (if you can&apos;t scan)
              </div>
              <code className="mt-0.5 block break-all rounded bg-background px-2 py-1 text-sm font-mono">
                {setupData.secret}
              </code>
            </div>
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-muted-foreground">
                otpauth URL (advanced)
              </summary>
              <code className="mt-1 block break-all rounded bg-background px-2 py-1 text-xs font-mono">
                {setupData.otpauthUrl}
              </code>
            </details>
          </div>

          <div>
            <div className="text-sm font-medium">2. Save your recovery codes</div>
            <p className="mt-1 text-xs text-muted-foreground">
              Each code works once if you lose access to your authenticator.{' '}
              <strong>This is the only time we'll show them.</strong> Store them somewhere safe.
            </p>
            <div className="mt-2 grid grid-cols-2 gap-1 rounded bg-background p-2 text-sm font-mono">
              {setupData.recoveryCodes.map((c) => (
                <div key={c}>{c}</div>
              ))}
            </div>
          </div>

          <div>
            <div id="twofa-enable-code-label" className="text-sm font-medium">
              3. Enter a code from your app to confirm
            </div>
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                aria-labelledby="twofa-enable-code-label"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={7}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                className="w-32 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-mono focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
              <Button type="button" onClick={confirmEnable} disabled={busy}>
                {busy ? 'Verifying…' : 'Confirm + enable'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {status === 'on' && (
        <div className="mt-3 space-y-3">
          <div className="rounded-md border border-emerald-500/30 bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-950/20 dark:text-emerald-200">
            Two-factor authentication is on. You'll need a code from your authenticator (or a
            recovery code) at sign-in.
          </div>
          <div>
            <div id="twofa-disable-code-label" className="text-sm font-medium">
              Disable 2FA
            </div>
            <p id="twofa-disable-code-help" className="mt-1 text-xs text-muted-foreground">
              Type a current 6-digit code from your authenticator (or one of your recovery codes) to
              confirm.
            </p>
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                aria-labelledby="twofa-disable-code-label"
                aria-describedby="twofa-disable-code-help"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456 or recovery code"
                className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-mono focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
              />
              <Button
                type="button"
                onClick={confirmDisable}
                disabled={busy}
                variant="outline"
                className="border-rose-500/40 text-rose-700 hover:bg-rose-500/10 dark:text-rose-300"
              >
                {busy ? 'Verifying…' : 'Disable'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
